import integrationUtils from './integrationUtils';

/** The root path used to access Documents. */
export const root = integrationUtils.getRoutesRoot() || '/ui/connect/';

export const paths = {
  workspace: '/',

  // admin section
  tenantSettings: '/tenant-settings',

  // sender section
  batchDocuments: (batchId = ':batchId') => `/outbox/${batchId}/documents`,
  approvalWorkflowAll: '/approval-workflow/all',
  approvalWorkflow: (batchId = ':batchId') => `/approval-workflow/cheque-batch-summary/${batchId}`,
  chequeBatchSummary: (batchId = ':batchId', tabId = ':tabId?', includeArchive = ':includeArchive?', manualDataEntryNewBatch = ':manualDataEntryNewBatch?') => (
    `/cheque-batch-summary/${batchId}/${tabId}/${includeArchive}/${manualDataEntryNewBatch}`
  ),
  newBatch: (typeFilter = ':typeFilter(documents|cheques)?') => `/new-batch/${typeFilter}`,
  outbox: '/outbox',
  insights: '/insights',
  cheques: '/cheques',
  archiveType: (type = ':type(documents|cheques|printing-services)?') => `/archive/${type}`,
  inbox: '/inbox',
  inboxReact: '/inboxReact',
  inboxRules: '/inbox/rules',
  bts: '/printing-services',
  btsBatch: (batchId = ':batchId', tabId = ':tabId?') => `/printing-services/${batchId}/${tabId}`,
  /**
   * inbox/:documentId is a legacy route to the document viewer. It needs
   * to be left in place to support links in old emails
   */
  inboxDocument: (documentId = ':document', tab = ':tab(history|information|additional-documents)?') => `/inbox/${documentId}/${tab}`,
  documentViewerBlank: '/document-viewer',
  documentViewer: (type = ':type', document = ':document', tab = ':tab(history|information|additional-documents)?') => `/document-viewer/${type}/${document}/${tab}`,
  contact: (contact = ':contact') => `/contacts/${contact}`,
  contacts: '/contacts',
  user: (user = ':user') => `/users/${user}`,
  users: '/users',
  people: '/people',
  profiles: '/profiles',
  profilesFullPath: '/ui/documents/profiles',
  profile: (type = ':type(standard|cheque)', profile = ':profile', tab = ':tab(setupTab|emailTab|paymentDetailsTab|presentationTab|accountDetailsTab|domLogsTab)?') => `/profiles/${type}/${profile}/${tab}`,
  btsprofiles: '/printing-services-profiles',
  btsprofile: (btsprofile = ':id') => `/printing-services-profiles/${btsprofile}`,
  chequebooks: '/chequebooks',
  chequebook: (id = ':id') => `/chequebooks/${id}`,
  linkAgents: '/link-agents',
  app: (app = ':app') => `/apps/${app}`,
  documentUnregistered: (secretId = ':secretId', secretPin = ':secretPin', tab = ':tab(additional-documents)?') => `/document/${secretId}/${secretPin}/${tab}`,
  logos: '/logos',
  logo: (logo = ':logo') => `/logos/${logo}`,
  roles: '/roles',
  role: (role = ':role') => `/roles/${role}`,
  reactRole: '/reactRole',
  printers: '/printers',
  printer: (printer = ':printer') => `/printers/${printer}`,
  printerQueue: '/printer-queue',
  profileTabs: (type = ':type', profile = ':profile', tab = ':tab') => `profiles/${type}/${profile}/${tab}`,
  projects: '/projects',
  project: (project = ':project') => `/projects/${project}`,
  projectFromGrid: (project = ':project') => `/ui/documents/projects/${project}`,
  projectsFromCancel: '/ui/documents/projects',
  adminProjects: '/projects',
  adminProjectFromGrid: (project = ':project') => `projects/${project}`,
  adminProjectsFromCancel: (realm = ':realm', tenantId = ':tenantId') => `/ui/realms/${realm}/companies/${tenantId}/documents/projects`,
  welcome: '/welcome',

  privacy: '/privacy',
  termsAndConditions: '/terms-conditions',
  userProfile: '/user-profile',
  whyRegister: '/why-register',
  webformsError: '/WebformsError',
  revokedNotice: '/RevokedNotice',
  outboxByMailingName: (mailingName = ':mailingName') => `/ui/documents/outbox?startIndex=0&limit=50&mailingName=${mailingName}&dir=out&groupBy=mailingId&ignoreCompleteArchivedBatches=true`,
  chequeBatchById: (batchId = ':batchId') => `/ui/documents/cheque-batch-summary/${batchId}`,
  adminUsers: '/ui/management/users',
  adminSingleUser: '/ui/management/users/view/',
  adminClient: (client = ':client') => `/ui/management/clients/${client}`
};

export const routes = Object.keys(paths).reduce((memo, key) => (
  [
    ...memo,
    {
      exact: true,
      path: (typeof paths[key] === 'function') ? paths[key]() : paths[key]
    }
  ]
), []);

routes.push({
  notFoundRoute: true
});

export default {
  // will be replaced from config leave it as default value
  // for integration though we may have a different default so use that in preference if it is set
  root,

  routes: integrationUtils.applyRouteUrlProcessors({
    // admin section
    tenantSettings: 'tenant-settings',

    // sender section
    batchDocuments: 'outbox/:batchId/documents',
    approvalWorkflowAll: 'approval-workflow/all',
    approvalWorkflow: 'approval-workflow/cheque-batch-summary/:batchId',
    chequeBatchSummaryBlank: 'cheque-batch-summary/:batchId',
    chequeBatchSummary: 'cheque-batch-summary/:batchId(/:tabId)(/:includeArchive)(/:manualDataEntryNewBatch)',
    newBatch: 'new-batch/:typeFilter',
    outbox: 'outbox',
    cheques: 'cheques',
    archive: 'archive',
    archiveType: 'archive/:type',
    inbox: 'inbox',
    bts: 'printing-services',
    btsBatch: 'printing-services/:batchId',
    btsBatchTabs: 'printing-services/:batchId/:tabId',
    /**
     * inbox/:documentId is a legacy route to the document viewer. It needs
     * to be left in place to support links in old emails
     */
    inboxDocument: 'inbox/:documentId',
    documentSummaryBlank: 'document-viewer/:type/:batchId',
    documentViewerBlank: 'document-viewer',
    documentViewer: 'document-viewer/:type/:document(/:tab)',
    documentViewerHistory: 'document-viewer/:type/:document/history',
    documentViewerInformation: 'document-viewer/:type/:document/information',
    documentViewerAdditionalDocuments: 'document-viewer/:type/:document/additional-documents',
    contact: 'contacts/:contact',
    contacts: 'contacts',
    user: 'users/:user',
    users: 'users',
    people: 'people',
    profiles: 'profiles',
    profile: 'profiles/:type/:profile',
    profileTabs: 'profiles/:type/:profile/:tab',
    btsprofiles: 'printing-services-profiles',
    btsprofile: 'printing-services-profiles/:btsprofile',
    chequebooks: 'chequebooks',
    chequebooksFullUrl: '/ui/documents/chequebooks',
    chequebook: 'chequebooks/:id',
    resetExpired: 'reset/expired',
    apps: 'apps',
    app: 'apps/:app',
    documentUnregistered: 'document/:secretId/:secretPin(/:tab)',
    additionalDocumentsUnregistered: 'document/:secretId/:secretPin/additional-documents',
    logos: 'logos',
    logo: 'logos/:logo',
    roles: 'roles',
    role: 'roles/:role',
    printers: '/ui/documents/printers',
    printer: 'printers/:printer',
    printerQueue: 'printer-queue',
    projects: 'projects',
    projectsFullUrl: '/ui/documents/projects',
    project: 'projects/:project',
    inboxRules: 'inbox/rules',

    privacy: 'privacy',
    termsAndConditions: 'terms-conditions',
    userProfile: 'user-profile',
    whyRegister: 'why-register'
  })
};
